(() => {
  'use strict';

  const SELECTOR = '[data-bw-ec-simulator]';

  class ProfitSimulator {
    constructor(root) {
      this.root = root;
      this.defaultMonth = root.dataset.defaultMonth || 'C1';
      
      this.state = {
        month: this.defaultMonth,
        firstWeekPurchase: null,
        newAffiliate: root.dataset.defaultNewAffiliate === 'true',
        startWeek: Number(root.dataset.defaultStartWeek) || 1,
        previousMonthInvite: root.dataset.defaultInvitedPrev === 'true',
        currentMonthInvite: root.dataset.defaultInvitedCurr === 'true',
        giftPriceDescription: root.dataset.giftPriceDescription || '',
        betterwarePointsDescription: root.dataset.betterwarePointsDescription || '',
        purchases: {}
      };

      this.months = [
        { id: "C1", name: "Enero (S1 a S5)", start: 1, end: 5 },
        { id: "C2", name: "Febrero (S6 a S9)", start: 6, end: 9 },
        { id: "C3", name: "Marzo (S10 a S14)", start: 10, end: 14 },
        { id: "C4", name: "Abril (S15 a S18)", start: 15, end: 18 },
        { id: "C5", name: "Mayo (S19 a S22)", start: 19, end: 22 },
        { id: "C6", name: "Junio (S23 a S26)", start: 23, end: 26 },
        { id: "C7", name: "Julio (S27 a S31)", start: 27, end: 31 },
        { id: "C8", name: "Agosto (S32 a S35)", start: 32, end: 35 },
        { id: "C9", name: "Septiembre (S36 a S39)", start: 36, end: 39 },
        { id: "C10", name: "Octubre (S40 a S44)", start: 40, end: 44 },
        { id: "C11", name: "Noviembre (S45 a S48)", start: 45, end: 48 },
        { id: "C12", name: "Diciembre (S49 a S53)", start: 49, end: 53 }
      ];

      this.rules = {
        giftRules: [
          { min: 50, max: 79, values: [4, 9, 12, 14, 14] },
          { min: 80, max: 119, values: [7, 12, 14, 20, 20] },
          { min: 120, max: Infinity, vals: [9, 14, 20, 26, 26] }
        ],
        speedometerRules: [
          { min: 50, max: 79, values: [0, 100, 500, 1000, 1000] },
          { min: 80, max: 119, values: [50, 100, 500, 2000, 2000] },
          { min: 120, max: Infinity, values: [100, 500, 1000, 3000, 3000] }
        ]
      };

      this.discounts = {};

      this.giftPrice = 2.5; // Fixed payout for "Ganar Más" program

      this.skipNextAutoSelect = false;

      this.currency = new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency: 'USD'
      });

      this.number = new Intl.NumberFormat('es-EC');

      this.elements = {
        month: root.querySelector('[data-field="month"]'),
        firstWeekPurchase: root.querySelector('[data-field="firstWeekPurchase"]'),
        startWeek: root.querySelector('[data-field="startWeek"]'),
        desktop: root.querySelector('[data-role="desktop-table"]'),
        mobile: root.querySelector('[data-role="mobile-cards"]'),
        summary: root.querySelector('[data-role="summary"]'),
        status: root.querySelector('[data-role="status"]')
      };

      
      this.bindEvents();
      this.updateToggleButtons();
      this.updateIngresoContainer();
      this.loadConfig();
    }

    populateStartWeeks() {
      if (!this.elements.startWeek) return;
      let html = '';
      for (let w = 20; w <= 53; w++) {
        html += `<option value="${w}">Semana ${w}</option>`;
      }
      this.elements.startWeek.innerHTML = html;
      this.elements.startWeek.value = String(this.state.startWeek);
    }

    bindEvents() {
      // Month select
      this.elements.month.addEventListener('change', (event) => {
        this.state.month = event.target.value;
        this.handleMonthChange();
      });

      // First week purchase select
      this.elements.firstWeekPurchase.addEventListener('change', (event) => {
        this.state.firstWeekPurchase = Number(event.target.value);
        this.state.purchases = {};
        this.calculate();
      });

      // Start week select
      this.elements.startWeek.addEventListener('change', (event) => {
        this.state.startWeek = Number(event.target.value);
        this.calculate();
      });

      // Toggle buttons
      this.root.addEventListener('click', (event) => {
        const toggleBtn = event.target.closest('[data-field-btn]');
        if (toggleBtn && this.root.contains(toggleBtn)) {
          const field = toggleBtn.dataset.fieldBtn;
          const value = toggleBtn.dataset.value === 'true';

          this.state[field] = value;
          this.updateToggleButtons();
          if (field === 'newAffiliate') {
            this.updateIngresoContainer();
          }
          this.calculate();
        }
      });

      // Purchase inputs
      this.root.addEventListener('input', (event) => {
        const input = event.target.closest('[data-week-input]');
        if (!input || !this.root.contains(input)) return;

        const week = String(input.dataset.weekInput);
        const digitsOnly = input.value.replace(/[^\d.]/g, '');

        this.state.purchases[week] = digitsOnly === '' ? '' : Math.max(0, Number(digitsOnly));
        this.calculate();
      });

      // Auto select on focus
      this.root.addEventListener('focusin', (event) => {
        const input = event.target.closest('[data-week-input]');
        if (!input) return;

        if (this.skipNextAutoSelect) {
          this.skipNextAutoSelect = false;
          return;
        }

        window.setTimeout(() => input.select(), 10);
      });
    }

    loadConfig() {
      this.setStatus('loading', 'Cargando configuración del simulador…');

      try {
        const data = this.readEmbeddedConfig();
        this.applyConfig(data);
        this.populateStartWeeks();
        this.populateMonths();
        this.clearStatus();
      } catch (error) {
        this.setStatus(
          'error',
          error instanceof Error ? error.message : 'No fue posible cargar la configuración.'
        );
      }
    }

     readEmbeddedConfig() {
      const script = this.root.querySelector('[data-role="config"]');
      const raw = script?.textContent?.trim();

      if (!raw) {
        throw new Error('Selecciona un metaobjeto de configuración para este simulador en el editor de temas.');
      }

      try {
        return JSON.parse(raw);
      } catch (error) {
        throw new Error('No fue posible interpretar la configuración del simulador.');
      }
    }

     applyConfig(data) {
      if (!data || data.active === false) {
        throw new Error('El simulador no está activo actualmente.');
      }

      if (!Array.isArray(data.months) || data.months.length === 0) {
        throw new Error('La configuración no contiene meses.');
      }

      this.months = data.months
        .map((month) => ({
          id: String(month.id),
          name: String(month.name),
          start: Number(month.start),
          end: Number(month.end)
        }))
        .filter((month) =>
          month.id &&
          Number.isInteger(month.start) &&
          Number.isInteger(month.end) &&
          month.end >= month.start
        );

      if (this.months.length === 0) {
        throw new Error('No existen meses válidos para mostrar.');
      }

      if (!data.discounts || typeof data.discounts !== 'object') {
        throw new Error('La configuración no contiene descuentos.');
      }

      this.discounts = {
        regular: typeof data.discounts.regular === 'number' ? data.discounts.regular : 0.24,
        invitation: typeof data.discounts.invitation === 'number' ? data.discounts.invitation : 0.26
      };

      if (!data.giftPrice || typeof data.giftPrice !== 'number') {
        throw new Error('La configuración no contiene el precio del regalo.');
      }

      this.giftPrice = data.giftPrice;

      this.rules = {
        giftRules: Array.isArray(data.giftRules) ? data.giftRules : [],
        speedometerRules: Array.isArray(data.speedometerRules) ? data.speedometerRules : []
      };

    }

    populateMonths() {
      this.elements.month.innerHTML = this.months
        .map((month) => (
          `<option value="${this.escapeHtml(month.id)}">${this.escapeHtml(month.name)} (S${month.start} a S${month.end})</option>`
        ))
        .join('');

      const monthExists = this.months.some((month) => month.id === this.defaultMonth);
      this.state.month = monthExists ? this.defaultMonth : this.months[0].id;
      this.elements.month.value = this.state.month;

      this.handleMonthChange(true);
    }
  
    handleMonthChange(initial = false) {
      const month = this.currentMonth();
      if (!month) return;

      this.populateWeeksPurchase(month);

      if (!initial) {
        this.state.purchases = {};
      }

      this.calculate();
    }

    populateWeeksPurchase(month) {
      const weeks = [];
      for (let week = month.start; week <= month.end; week += 1) {
        weeks.push(week);
      }

      this.elements.firstWeekPurchase.innerHTML = weeks
        .map((week) => `<option value="${week}">Semana ${week}</option>`)
        .join('');

      this.elements.firstWeekPurchase.disabled = false;

      const currentIsValid = weeks.includes(Number(this.state.firstWeekPurchase));
      this.state.firstWeekPurchase = currentIsValid
        ? Number(this.state.firstWeekPurchase)
        : month.start;

      this.elements.firstWeekPurchase.value = String(this.state.firstWeekPurchase);
    }

    currentMonth() {
      return this.months.find((month) => month.id === this.state.month) || null;
    }

    activeWeeks() {
      const month = this.currentMonth();
      if (!month || !Number.isInteger(this.state.firstWeekPurchase)) return [];

      const weeks = [];
        for (let week = this.state.firstWeekPurchase; week <= month.end; week += 1) {
          weeks.push(week);
        }
      return weeks;
    }

    updateToggleButtons() {
      const fields = ['newAffiliate', 'previousMonthInvite', 'currentMonthInvite'];
      fields.forEach((field) => {
        const val = this.state[field];
        this.root.querySelectorAll(`[data-field-btn="${field}"]`).forEach((btn) => {
          const btnVal = btn.dataset.value === 'true';
          btn.classList.toggle('is-active', btnVal === val);
          btn.setAttribute('aria-pressed', String(btnVal === val));
        });
      });
    }

    updateIngresoContainer() {
      const container = this.root.querySelector('[data-container="startWeek"]');
      if (container) {
        container.style.opacity = this.state.newAffiliate ? '1' : '0.4';
        container.style.pointerEvents = this.state.newAffiliate ? 'auto' : 'none';
      }
    }

    findRule(amount, rules) {
      return  rules.find((rule) =>
        amount >= rule.min && (rule.max == null || amount <= rule.max)
      );
    }

    validateState() {
      if (!this.rules) return 'La configuración del simulador aún no está lista.';
      if (!this.state.month) return 'Selecciona un mes.';
      if (!Number.isInteger(this.state.firstWeekPurchase)) return 'Selecciona una semana inicial.';
      if (!Number.isInteger(this.state.startWeek)) return 'Selecciona una semana de ingreso.';

      for (const week of this.activeWeeks()) {
        const raw = this.state.purchases[String(week)];
        if (raw === '' || raw === undefined) continue;
        if (!Number.isFinite(Number(raw)) || Number(raw) < 0) {
          return `El monto de la semana ${week} no es válido.`;
        }
      }

      return null;
    }

    calculate() {
      const validationError = this.validateState();

      if (validationError) {
        this.setStatus('error', validationError);
        return;
      }

      const focusState = this.captureFocusState();
      this.computeResult();

      this.restoreFocusState(focusState);
      this.clearStatus();
    }

     captureFocusState() {
      const active = document.activeElement;
      if (!active || !this.root.contains(active) || !active.matches('[data-week-input]')) {
        return null;
      }

      const atEnd = active.selectionStart === active.value.length && active.selectionEnd === active.value.length;

      return {
        week: active.dataset.weekInput,
        context: active.classList.contains('bwec-profit-simulator__week-input--desktop') ? 'desktop' : 'mobile',
        selectionStart: active.selectionStart,
        selectionEnd: active.selectionEnd,
        atEnd
      };
    }

     restoreFocusState(focusState) {
      if (!focusState) return;

      const selector = `[data-week-input="${CSS.escape(focusState.week)}"].bwec-profit-simulator__week-input--${focusState.context}`;
      const input = this.root.querySelector(selector);

      if (!input) return;

      this.skipNextAutoSelect = true;
      input.focus();

      const length = input.value.length;
      const start = focusState.atEnd ? length : Math.min(focusState.selectionStart, length);
      const end = focusState.atEnd ? length : Math.min(focusState.selectionEnd, length);

      try {
        input.setSelectionRange(start, end);
      } catch (error) {
        // Algunos navegadores/tipos de input no soportan rangos de selección; el foco ya quedó restaurado.
      }
    }

    computeResult() {
      const month = this.currentMonth();
      if (!month) return;

      const weeks = this.activeWeeks();
      const descuentoPct = this.state.previousMonthInvite ? this.discounts.invitation : this.discounts.regular;

      let semanaActiva = 0;
      const resultados = [];
      const t = {
        compra: 0,
        descNeto: 0,
        precioCat: 0,
        pagoRegalo: 0,
        gananciaRegalo: 0,
        basePB: 0,
        velocimetro: 0,
        bonoNueva: 0
      };

      for (const s of weeks) {
        const monto = parseFloat(this.state.purchases[s]) || 0;
        const descNeto = monto * descuentoPct;
        const basePB = Math.ceil(monto);

        let precioCat = 0,
          pagoRegalo = 0,
          gananciaRegalo = 0,
          velocimetro = 0;

        if (monto > 0) {
          semanaActiva++;
          const idx = Math.min(semanaActiva - 1, 4);

          const rGanarMas = this.findRule(monto, this.rules.giftRules);
          if (rGanarMas) {
            precioCat = rGanarMas.values[idx];
            pagoRegalo = this.giftPrice;
            gananciaRegalo = precioCat - pagoRegalo;
          }

          const rVelo = this.findRule(monto, this.rules.speedometerRules);
          if (rVelo) {
            velocimetro = rVelo.values[idx];
          }
        }

        // Welcome bonus (first 6 weeks from ingress)
        let bonoNueva = 0;
        if (
          this.state.newAffiliate &&
          this.state.startWeek &&
          s >= this.state.startWeek &&
          s - this.state.startWeek < 6
        ) {
          bonoNueva = basePB + velocimetro;
        }

        t.compra += monto;
        t.descNeto += descNeto;
        t.precioCat += precioCat;
        t.pagoRegalo += pagoRegalo;
        t.gananciaRegalo += gananciaRegalo;
        t.basePB += basePB;
        t.velocimetro += velocimetro;
        t.bonoNueva += bonoNueva;

        resultados.push({
          semanaId: s,
          monto,
          descNeto,
          precioCat,
          pagoRegalo,
          gananciaRegalo,
          basePB,
          velocimetro,
          bonoNueva,
        });
      }

      const extraInvitado = this.state.currentMonthInvite ? t.velocimetro : 0;
      const pbFinal = t.basePB + t.velocimetro + extraInvitado + t.bonoNueva;
      const beneficioEfectivo = t.descNeto + t.gananciaRegalo;

      const baseComparacion = t.compra + t.precioCat;
      const porcentajeGanancia = baseComparacion > 0 ? (beneficioEfectivo / baseComparacion) * 100 : 0;

      const totals = {
        compra: t.compra,
        descNeto: t.descNeto,
        precioCat: t.precioCat,
        pagoRegalo: t.pagoRegalo,
        gananciaRegalo: t.gananciaRegalo,
        basePB: t.basePB,
        velocimetro: t.velocimetro,
        bonoNueva: t.bonoNueva,
        extraInvitado,
        pbFinal,
        beneficioEfectivo,
        porcentajeGanancia
      };

      // Save focused element
      const activeElement = document.activeElement;
      const activeId = activeElement ? activeElement.id : null;
      const selectionStart = activeElement ? activeElement.selectionStart : null;
      const selectionEnd = activeElement ? activeElement.selectionEnd : null;

      this.renderDesktopTable({ resultados, totales: totals });
      this.renderMobileCards({ resultados, totales: totals });
      this.renderSummary({ resultados, totales: totals });

      // Restore focus
      if (activeId) {
        const newActive = this.root.querySelector(`#${activeId}`);
        if (newActive) {
          this.skipNextAutoSelect = true;
          newActive.focus();
          try {
            if (newActive.setSelectionRange && selectionStart !== null && selectionEnd !== null) {
              newActive.setSelectionRange(selectionStart, selectionEnd);
            }
          } catch (e) {
            // No selection range for number input
          }
        }
      }
    }

    renderDesktopTable(data) {
      const weeks = this.activeWeeks();

      if (weeks.length === 0) {
        this.elements.desktop.innerHTML = '';
        return;
      }

      const rows = data?.resultados || [];
      const totals = data?.totales || this.emptyTotals();
      const byWeek = new Map(rows.map((row) => [Number(row.semanaId), row]));
      const descuentoPct = this.state.previousMonthInvite ? this.discounts.invitation * 100 : this.discounts.regular * 100;

      this.elements.desktop.innerHTML = `
        <div class="bwec-profit-simulator__table-scroll">
          <table class="bwec-profit-simulator__table">
            <thead>
              <tr>
                <th class="bwec-profit-simulator__sticky bwec-profit-simulator__concept-head">Concepto</th>
                ${weeks.map((week) => `<th class="text-center">S${week}</th>`).join('')}
                <th class="bwec-profit-simulator__total-head text-center">Total Mes</th>
              </tr>
            </thead>
            <tbody>
              <!-- MONTO DE COMPRA -->
              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--strong">Monto de Compra</td>
                ${weeks.map((week) => `
                  <td class="bwec-profit-simulator__purchase-cell">
                    ${this.weekInput(week, 'desktop')}
                  </td>
                `).join('')}
                <td class="bwec-profit-simulator__total-cell bwec-profit-simulator__total-cell--green text-base">
                  ${this.currency.format(totals.compra || 0)}
                </td>
              </tr>

              <!-- DESCUENTO NETO -->
              <tr class="bwec-profit-simulator__row-soft">
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept">Descuento Neto (${descuentoPct}%)</td>
                ${weeks.map((week) => `
                  <td class="text-center">${this.currency.format(byWeek.get(week)?.descNeto || 0)}</td>
                `).join('')}
                <td class="bwec-profit-simulator__total-cell font-bold text-center">
                  ${this.currency.format(totals.descNeto || 0)}
                </td>
              </tr>

              <!-- GANAR MAS -->
              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__section-label bwec-profit-simulator__section-label--orange">
                  Programa Ganar Más
                </td>
                <td colspan="${weeks.length + 1}" class="bwec-profit-simulator__section-note bwec-profit-simulator__section-note--orange text-left">
                  ${this.state.giftPriceDescription} 
                </td>
              </tr>

              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--indent text-left pl-4">Valor del Producto</td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.precioCat || 0;
                  return `<td class="bwec-profit-simulator__strike text-center">${value > 0 ? this.currency.format(value) : '-'}</td>`;
                }).join('')}
                <td class="text-gray-500 text-center">${this.currency.format(totals.precioCat || 0)}</td>
              </tr>

              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--indent text-left pl-4">Precio de Regalo</td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.pagoRegalo || 0;
                  return `<td class="bwec-profit-simulator__negative text-center">${value > 0 ? `-${this.currency.format(value)}` : '-'}</td>`;
                }).join('')}
                <td class="bwec-profit-simulator__negative text-center">-${this.currency.format(totals.pagoRegalo || 0)}</td>
              </tr>

              <tr class="bwec-profit-simulator__row-orange">
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--indent bwec-profit-simulator__orange pl-4 font-semibold">
                  Ahorro Ganar Más
                </td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.gananciaRegalo || 0;
                  return `<td class="bwec-profit-simulator__orange bwec-profit-simulator__strong text-center">${value > 0 ? this.currency.format(value) : '-'}</td>`;
                }).join('')}
                <td class="bwec-profit-simulator__total-cell font-bold text-center">
                  ${this.currency.format(totals.gananciaRegalo || 0)}
                </td>
              </tr>

              <!-- PUNTOS BETTERWARE -->
              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__section-label bwec-profit-simulator__section-label--green">
                  Puntos Betterware
                </td>
                <td colspan="${weeks.length + 1}" class="bwec-profit-simulator__section-note bwec-profit-simulator__section-note--green text-left">
                  ${this.state.betterwarePointsDescription}
                </td>
              </tr>

              <tr>
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--indent text-left pl-4">1 PB x $1</td>
                ${weeks.map((week) => `<td class="text-center">${this.number.format(byWeek.get(week)?.basePB || 0)}</td>`).join('')}
                <td class="text-center">${this.number.format(totals.basePB || 0)}</td>
              </tr>

              <tr class="bwec-profit-simulator__row-green">
                <td class="bwec-profit-simulator__sticky bwec-profit-simulator__concept bwec-profit-simulator__concept--indent bwec-profit-simulator__green bwec-profit-simulator__strong pl-4 text-left">
                  Velocímetro 
                  <svg class="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"></path></svg>
                </td>
                ${weeks.map((week) => `<td class="bwec-profit-simulator__green bwec-profit-simulator__strong text-center">${this.number.format(byWeek.get(week)?.velocimetro || 0)}</td>`).join('')}
                <td class="bwec-profit-simulator__strong text-center">${this.number.format(totals.velocimetro || 0)}</td>
              </tr>

              ${this.state.newAffiliate && totals.bonoNueva > 0 ? `
                <tr class="bwec-profit-simulator__row-purple">
                  <td class="bwec-profit-simulator__sticky bwec-profit-simulator__purple pl-4 text-left bwec-profit-simulator__strong">
                    Bono Bienvenida (1as 6 sem.)
                  </td>
                  ${weeks.map((week) => {
                    const val = byWeek.get(week)?.bonoNueva || 0;
                    return `<td class="bwec-profit-simulator__purple bwec-profit-simulator__strong text-center">${val > 0 ? this.number.format(val) : '-'}</td>`;
                  }).join('')}
                  <td class="bwec-profit-simulator__purple bwec-profit-simulator__strong text-center">
                    +${this.number.format(totals.bonoNueva || 0)}
                  </td>
                </tr>
              ` : ''}

              ${this.state.currentMonthInvite ? `
                <tr class="bwec-profit-simulator__row-purple">
                  <td colspan="${weeks.length + 1}" class="bwec-profit-simulator__bonus-label bwec-profit-simulator__strong text-right text-xs md:text-sm bwec-profit-simulator__purple">
                    Bono Acelera x2 (Por Invitado) -&gt;
                  </td>
                  <td class="bwec-profit-simulator__purple bwec-profit-simulator__strong text-center">
                    +${this.number.format(totals.extraInvitado || 0)}
                  </td>
                </tr>
              ` : ''}
            </tbody>
          </table>
        </div>
      `;
    }

    renderMobileCards(data) {
      const weeks = this.activeWeeks();
      const rows = data?.resultados || [];
      const totals = data?.totales || this.emptyTotals();
      const byWeek = new Map(rows.map((row) => [Number(row.semanaId), row]));
      const descuentoPct = this.state.previousMonthInvite ? this.discounts.invitation * 100 : this.discounts.regular * 100;

      this.elements.mobile.innerHTML = `
        <div class="bwec-profit-simulator__mobile-stack">
          ${weeks.map((week) => {
            const row = byWeek.get(week) || {};
            return `
              <article class="bwec-profit-simulator__week-card">
                <header class="bwec-profit-simulator__week-card-header">SEMANA S${week}</header>
                <div class="bwec-profit-simulator__week-card-body">
                  <div class="bwec-profit-simulator__mobile-purchase">
                    <label>Monto de Compra</label>
                    ${this.weekInput(week, 'mobile')}
                  </div>

                  <div class="bwec-profit-simulator__mobile-result bwec-profit-simulator__mobile-result--neutral">
                    <span>Descuento Neto (${descuentoPct}%)</span>
                    <strong>${this.currency.format(row.descNeto || 0)}</strong>
                  </div>

                  <div class="bwec-profit-simulator__mobile-result bwec-profit-simulator__mobile-result--orange">
                    <span class="bwec-profit-simulator__mobile-kicker">Ganar Más</span>
                    <div><span>Valor Producto:</span><span class="bwec-profit-simulator__strike">${row.precioCat ? this.currency.format(row.precioCat) : '-'}</span></div>
                    <div><span>Precio de Regalo:</span><span class="bwec-profit-simulator__negative">${row.pagoRegalo ? `-${this.currency.format(row.pagoRegalo)}` : '-'}</span></div>
                    <div class="bwec-profit-simulator__mobile-result-total">
                      <span>Ahorro:</span>
                      <strong>${row.gananciaRegalo ? this.currency.format(row.gananciaRegalo) : '-'}</strong>
                    </div>
                  </div>

                  <div class="bwec-profit-simulator__mobile-result bwec-profit-simulator__mobile-result--green">
                    <span class="bwec-profit-simulator__mobile-kicker">Puntos Betterware</span>
                    <div><span>1 PB x $1:</span><span>${this.number.format(row.basePB || 0)} PB</span></div>
                    <div class="bwec-profit-simulator__mobile-result-total">
                      <span>Velocímetro</span>
                      <strong>${this.number.format(row.velocimetro || 0)} PB</strong>
                    </div>
                    ${this.state.newAffiliate && row.bonoNueva > 0 ? `
                      <div class="bwec-profit-simulator__mobile-result-total bwec-profit-simulator__purple pt-1" style="border-top: 1px border-green-150;">
                        <span>Bono Bienvenida:</span>
                        <strong>+${this.number.format(row.bonoNueva)} PB</strong>
                      </div>
                    ` : ''}
                  </div>
                </div>
              </article>
            `;
          }).join('')}

          <article class="bwec-profit-simulator__catalog-card">
            <header class="bwec-profit-simulator__catalog-card-header">
              <strong>Resumen Total Mes</strong>
            </header>
            <div class="bwec-profit-simulator__catalog-card-body">
              <div class="bwec-profit-simulator__catalog-line">
                <span>Monto total de compra:</span>
                <strong class="bwec-profit-simulator__blue">${this.currency.format(totals.compra || 0)}</strong>
              </div>
              <div class="bwec-profit-simulator__catalog-line">
                <span>Descuento Neto:</span>
                <strong>${this.currency.format(totals.descNeto || 0)}</strong>
              </div>

              <div class="bwec-profit-simulator__mobile-result bwec-profit-simulator__mobile-result--orange">
                <span class="bwec-profit-simulator__mobile-kicker">Ganar Más</span>
                <div><span>Valor Producto:</span><span class="bwec-profit-simulator__strike">${this.currency.format(totals.precioCat || 0)}</span></div>
                <div><span>Precio de Regalo:</span><span class="bwec-profit-simulator__negative">-${this.currency.format(totals.pagoRegalo || 0)}</span></div>
                <div class="bwec-profit-simulator__mobile-result-total">
                  <span>Ahorro:</span>
                  <strong>${this.currency.format(totals.gananciaRegalo || 0)}</strong>
                </div>
              </div>

              <div class="bwec-profit-simulator__mobile-result bwec-profit-simulator__mobile-result--green">
                <span class="bwec-profit-simulator__mobile-kicker">Puntos Betterware (PB)</span>
                <div><span>PB por compra:</span><span>${this.number.format(totals.basePB || 0)} PB</span></div>
                <div><span>Velocímetro:</span><span>${this.number.format(totals.velocimetro || 0)} PB</span></div>
                ${this.state.currentMonthInvite ? `<div class="bwec-profit-simulator__purple"><span>Bono Acelera x2:</span><span>+${this.number.format(totals.extraInvitado || 0)} PB</span></div>` : ''}
                ${this.state.newAffiliate && totals.bonoNueva > 0 ? `<div class="bwec-profit-simulator__purple"><span>Bono Bienvenida:</span><span>+${this.number.format(totals.bonoNueva || 0)} PB</span></div>` : ''}
                <div class="bwec-profit-simulator__mobile-result-total">
                  <span>Puntos totales:</span>
                  <strong>${this.number.format(totals.pbFinal || 0)} PB</strong>
                </div>
              </div>
            </div>
          </article>
        </div>
      `;
    }

    renderSummary(data) {
      const totals = data?.totales || this.emptyTotals();

      this.elements.summary.innerHTML = `
        <article class="bwec-profit-simulator__summary-card">
          <span class="bwec-profit-simulator__summary-decoration bwec-profit-simulator__summary-decoration--orange"></span>
          <h3>BENEFICIO ESTIMADO</h3>
          <strong class="bwec-profit-simulator__summary-value bwec-profit-simulator__orange">
            ${this.currency.format(totals.beneficioEfectivo || 0)}
          </strong>
          <span class="bwec-profit-simulator__summary-pill">Descuento + Ganar Más</span>
        </article>

        <article class="bwec-profit-simulator__summary-card bwec-profit-simulator__summary-card--featured">
          <span class="bwec-profit-simulator__summary-decoration bwec-profit-simulator__summary-decoration--white"></span>
          <h3>PORCENTAJE DE GANANCIA ESTIMADA</h3>
          <div class="bwec-profit-simulator__percentage font-black">
            <strong>${Number(totals.porcentajeGanancia || 0).toFixed(1)}</strong><span>%</span>
          </div>
          <p>
            Sobre tu compra total más el valor de catálogo del producto recibido 
            (${this.currency.format(totals.compra + totals.precioCat)}), sujeto al cumplimiento de las reglas comerciales.
          </p>
        </article>

        <article class="bwec-profit-simulator__summary-card">
          <span class="bwec-profit-simulator__summary-decoration bwec-profit-simulator__summary-decoration--green"></span>
          <h3>PUNTOS TOTALES</h3>
          <div class="bwec-profit-simulator__points">
            <strong>${this.number.format(totals.pbFinal || 0)}</strong>
            <span class="bwec-profit-simulator__points-badge">PB</span>
          </div>
          <span class="bwec-profit-simulator__summary-pill">¡Listo para canjear premios!</span>
        </article>
      `;
    }

    emptyTotals() {
      return {
        compra: 0,
        descNeto: 0,
        precioCat: 0,
        pagoRegalo: 0,
        gananciaRegalo: 0,
        basePB: 0,
        velocimetro: 0,
        bonoNueva: 0,
        extraInvitado: 0,
        pbFinal: 0,
        beneficioEfectivo: 0,
        porcentajeGanancia: 0
      };
    }

    weekInput(week, idPrefix) {
      const val = this.state.purchases[week] !== undefined ? this.state.purchases[week] : '';
      return `
        <input
          type="text"
          inputmode="decimal"
          autocomplete="off"
          data-week-input="${week}"
          id="bwec-input-${idPrefix}-${week}"
          value="${val}"
          placeholder="$0"
          class="bwec-profit-simulator__week-input bwec-profit-simulator__week-input--${idPrefix}"
          aria-label="Monto de compra semana ${week}"
        >
      `;
    }

    setStatus(type, message) {
      this.elements.status.hidden = false;
      this.elements.status.className = `bwec-profit-simulator__status is-${type}`;
      this.elements.status.textContent = message;
    }

    clearStatus() {
      this.elements.status.hidden = true;
      this.elements.status.textContent = '';
      this.elements.status.className = 'bwec-profit-simulator__status';
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach((element) => {
      if (element.dataset.bwProfitSimulatorInitialized === 'true') return;
      element.dataset.bwProfitSimulatorInitialized = 'true';
      new ProfitSimulator(element);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();